import * as React from "react";
import Table from "react-bootstrap/Table";
import Button from "@mui/material/Button";
import Pagination from "@mui/material/Pagination";
import { NavLink } from "react-router-dom";
import Col from "react-bootstrap/Col";

import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { postRequest } from "../../lib/api";
import { AuthContext } from "../../authContext/context";
import { useContext, useEffect, useRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import BreadcrumbsWrapper from "../../components/breadcrumbs";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { checkPermission } from "../../utils/checkPermissions";
import PermissionWrapper from "../../components/PermissionWrapper";

export default function Customers() {
  const authCtx = useContext(AuthContext);
  const userPermissions = authCtx.permissions;

  // Check permissions
  const canCreateLeads = checkPermission(userPermissions, "leads", "create");
  const canViewLeads = checkPermission(userPermissions, "leads", "view");
  const canEditLeads = checkPermission(userPermissions, "leads", "edit");
  const canDeleteLeads = checkPermission(userPermissions, "leads", "delete");

  const [productList, setProductList] = useState([]);
  const [groupCodeList, setGroupCodeList] = useState([]);
  const [leadList, setLeadList] = useState([]);
  const [leadPage, setLeadPage] = useState(1);
  const [leadPageSize, setLeadPageSize] = useState(10);
  const [leadTotalPages, setLeadTotalPages] = useState(1);
  const [leadTotalDocs, setLeadTotalDocs] = useState(0);
  const [leadLoading, setLeadLoading] = useState(true);

  const [filters, setFilters] = useState({
    lead_id: "",
    customer_name: "",
    product_id: "",
    groupcode_id: "",
  });

  // Debouncing for search fields
  const debounceRef = useRef();
  const [searchFilters, setSearchFilters] = useState({
    lead_id: "",
    customer_name: "",
  });

  const fetchDropdowns = async () => {
    try {
      const [productRes, groupCodeRes] = await Promise.all([
        postRequest("private/getproductList", {}, authCtx.token),
        postRequest("private/getgroupcodeList", {}, authCtx.token),
      ]);

      if (productRes?.data) setProductList(productRes.data);
      if (groupCodeRes?.data) setGroupCodeList(groupCodeRes.data);
    } catch (err) {
      console.error("Dropdown fetch error:", err);
    }
  };

  useEffect(() => {
    fetchDropdowns();
  }, []);

  const fetchLeads = async () => {
    setLeadLoading(true);

    // Clean up filters - remove empty values
    const cleanedFilters = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key] && filters[key].toString().trim() !== "") {
        cleanedFilters[key] = filters[key].toString().trim();
      }
    });

    const payload = {
      page: leadPage,
      limit: leadPageSize,
      ...cleanedFilters,
    };

    console.log("🔍 === REF FILTER DEBUG ===");
    console.log("REF Search Value:", filters.lead_id);
    console.log("All Applied Filters:", cleanedFilters);
    console.log("API Payload:", payload);

    try {
      // Fetch regular leads
      const res = await postRequest("private/leadList", payload, authCtx.token);
      console.log("📡 API Response Status:", res?.status);
      console.log("📡 API Response Data Count:", res?.data?.docs?.length || 0);
      console.log("📡 API Response Total Docs:", res?.data?.totalDocs || 0);

      if (res?.data && res.data.docs) {
        let leads = res.data.docs;

        // Log all leads with their product titles to debug conveyancing
        console.log("🔍 ALL LEADS FROM API WITH PRODUCTS:");
        leads.forEach((lead, index) => {
          console.log(
            `${index + 1}. Lead ID: ${lead.lead_id}, Product: "${lead.product_title}", Customer: ${lead.customer_name}`,
          );
        });

        // Commented out the separate conveyancing API call
        // Only process conveyancing leads that come from the main leadList API
        const existingConveyancing = leads.filter(
          (lead) =>
            lead?.product_title?.toLowerCase().includes("conveyancing") ||
            lead?.product_title?.toLowerCase().includes("conveycing"),
        );

        if (existingConveyancing.length > 0) {
          console.log(
            "✅ Found conveyancing leads in main API:",
            existingConveyancing.length,
          );
          existingConveyancing.forEach((lead) => {
            console.log(`  - ${lead.lead_id}: ${lead.product_title}`);
            lead.is_conveyancing = true;
            // Update product title to include service type if available
            if (lead.service_type) {
              lead.product_title =
                "Conveyancing (" + lead.service_type.replace(/_/g, " ") + ")";
              console.log(`  - Updated title to: ${lead.product_title}`);
            }
          });
        }

        console.log("📋 FINAL LEADS LIST (with products):");
        leads.forEach((lead, index) => {
          console.log(
            `${index + 1}. ${lead.lead_id} - ${lead.product_title} - ${lead.customer_name}`,
          );
        });

        // Search (lead_id / customer_name) is now handled by the backend across
        // the whole collection, so we use the returned page as-is.
        setLeadList(leads);
        setLeadTotalPages(res.data.totalPages || 1);
        setLeadTotalDocs(res.data.totalDocs || leads.length);
        console.log("✅ Final Results Count:", leads.length);
        console.log("✅ Total Docs in DB:", res.data.totalDocs || leads.length);

        if (
          leads.length === 0 &&
          (cleanedFilters.lead_id || cleanedFilters.customer_name)
        ) {
          console.log("❌ No matches found for your search criteria");
        }
      } else {
        console.log("❌ No data received from API");
        setLeadList([]);
        setLeadTotalPages(1);
        setLeadTotalDocs(0);
      }
    } catch (err) {
      console.error("❌ API Error:", err);
      setLeadList([]);
      setLeadTotalPages(1);
      setLeadTotalDocs(0);
    } finally {
      setLeadLoading(false);
    }
  };

  const slugify = (text) =>
    text
      ?.toLowerCase()
      ?.replace(/ /g, "-") // spaces to hyphen
      ?.replace(/[^\w-]+/g, "") // remove special chars
      ?.replace(/-+/g, "-"); // collapse consecutive hyphens to single hyphen

  useEffect(() => {
    fetchLeads();
  }, [leadPage, leadPageSize, filters]);

  // Change how many records show per page (resets to first page)
  const handleLeadPageSizeChange = (event) => {
    setLeadPageSize(Number(event.target.value));
    setLeadPage(1);
  };

  // Debounce search filters
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      console.log("=== DEBOUNCE SEARCH FILTERS ===");
      console.log("Search filters to apply:", searchFilters);

      setFilters((prev) => {
        const newFilters = {
          ...prev,
          lead_id: searchFilters.lead_id.trim(),
          customer_name: searchFilters.customer_name.trim(),
        };
        console.log("Updated filters:", newFilters);
        return newFilters;
      });
      setLeadPage(1); // Reset to first page when filtering
    }, 500); // 500ms debounce

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchFilters]);

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;

    // Handle search fields with debouncing
    if (field === "lead_id" || field === "customer_name") {
      setSearchFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else {
      // Handle dropdown fields immediately
      setFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
      setLeadPage(1);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      lead_id: "",
      customer_name: "",
      product_id: "",
      groupcode_id: "",
    });
    setSearchFilters({
      lead_id: "",
      customer_name: "",
    });
    setLeadPage(1);
  };

  const handleLeadPageChange = (event, value) => {
    setLeadPage(value);
  };

  // Detect whether a lead is a General Enquiry (Net Fee & Status don't apply to them)
  const isGeneralEnquiryLead = (lead) => {
    let productTitle = lead?.product_title;
    if (!productTitle && lead?.product_id && productList.length > 0) {
      productTitle = productList.find((p) => p._id === lead.product_id)?.title;
    }
    return productTitle?.toLowerCase() === "general enquiry";
  };

  // Hide Net Fee & Status columns entirely only when the list is purely General Enquiry
  const hideFeeStatus =
    leadList.length > 0 && leadList.every(isGeneralEnquiryLead);
  const leadColSpan = hideFeeStatus ? 9 : 11;

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: "Dashboard", path: "/dashboard" },
          { label: "Leads", path: "" },
        ]}
      />
      <br></br>
      <div className="page-header pb-3">
        <h1 className="mt-0 mb-0">Leads</h1>
        <div className="header-right-btn"></div>
      </div>

      {/* Search Filters */}
      <div className="page-top-formview mb-3">
        <Form>
          <Row className="mb-3 align-items-end">
            <Form.Group as={Col}>
              <FormControl fullWidth>
                <TextField
                  label="Ref (Lead ID)"
                  type="text"
                  value={searchFilters.lead_id}
                  onChange={handleFilterChange("lead_id")}
                  placeholder="Enter Lead ID (e.g. L-123)"
                  size="small"
                />
              </FormControl>
            </Form.Group>

            <Form.Group as={Col}>
              <FormControl fullWidth size="small">
                <InputLabel>Product</InputLabel>
                <Select
                  value={filters.product_id}
                  label="Product"
                  onChange={handleFilterChange("product_id")}
                  size="small"
                >
                  <MenuItem value="">
                    <em>All Products</em>
                  </MenuItem>
                  {productList.map((p) => (
                    <MenuItem key={p._id} value={p._id}>
                      {p.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Form.Group>

            <Form.Group as={Col}>
              <FormControl fullWidth>
                <TextField
                  label="Customer Name"
                  value={searchFilters.customer_name}
                  onChange={handleFilterChange("customer_name")}
                  placeholder="Enter customer name"
                  size="small"
                />
              </FormControl>
            </Form.Group>

            <Form.Group as={Col}>
              <FormControl fullWidth size="small">
                <InputLabel>Group Code</InputLabel>
                <Select
                  value={filters.groupcode_id}
                  label="Group Code"
                  onChange={handleFilterChange("groupcode_id")}
                  size="small"
                >
                  <MenuItem value="">
                    <em>All Group Codes</em>
                  </MenuItem>
                  {groupCodeList.map((g) => (
                    <MenuItem key={g._id} value={g._id}>
                      {g.name} ({g.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Form.Group>

            {/* Clear Button Column */}
            <Form.Group as={Col} xs="auto" className="d-flex align-items-end">
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                size="small"
                style={{
                  borderColor: "#6c757d",
                  color: "#6c757d",
                  height: "40px",
                  minWidth: "110px",
                }}
              >
                Clear Filters
              </Button>
            </Form.Group>
          </Row>

          {/* Results Count */}
          {leadList.length > 0 && (
            <Row>
              <Col>
                <span className="text-muted small">
                  {leadTotalDocs > 0 ? (
                    <>
                      Showing {(leadPage - 1) * leadPageSize + 1}-
                      {Math.min(leadPage * leadPageSize, leadTotalDocs)} of{" "}
                      {leadTotalDocs} leads
                    </>
                  ) : (
                    "No leads found"
                  )}
                </span>
              </Col>
            </Row>
          )}
        </Form>

        {/* Leads Table */}
        <div className="customer-tab-view">
          <Table striped responsive bordered>
            <thead>
              <tr>
                <th colSpan={leadColSpan} className="bg-grey">
                  Leads
                </th>
              </tr>
              <tr>
                <th>Ref</th>
                <th>Product</th>
                <th>Customer</th>
                {!hideFeeStatus && <th>Net Fee</th>}
                {/* <th>Total</th> */}
                {!hideFeeStatus && <th>Status</th>}
                <th>Created</th>
                <th>Group Code</th>
                <th>Created By</th>
                <th>Contact Attempts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leadLoading ? (
                <tr>
                  <td colSpan={leadColSpan} className="text-center">
                    <CircularProgress />
                  </td>
                </tr>
              ) : leadList.length === 0 ? (
                <tr>
                  <td colSpan={leadColSpan} className="text-center">
                    No Leads Found
                  </td>
                </tr>
              ) : (
                leadList.map((lead) => {
                  // Detect General Enquiry leads — Net Fee & Status are not applicable for them
                  let geProductTitle = lead?.product_title;
                  if (
                    !geProductTitle &&
                    lead?.product_id &&
                    productList.length > 0
                  ) {
                    geProductTitle = productList.find(
                      (p) => p._id === lead.product_id,
                    )?.title;
                  }
                  const isGeneralEnquiry =
                    geProductTitle?.toLowerCase() === "general enquiry";
                  return (
                    <tr key={lead._id}>
                      <td>{lead?.lead_id}</td>
                      <td>
                        {(() => {
                          // Get product title from productList using product_id
                          let productTitle = lead?.product_title;

                          // If no product_title but we have product_id, look it up in productList
                          if (
                            !productTitle &&
                            lead?.product_id &&
                            productList.length > 0
                          ) {
                            const product = productList.find(
                              (p) => p._id === lead.product_id,
                            );
                            productTitle = product?.title || "Unknown Product";
                          }

                          // Fix spelling of Conveycing to Conveyancing
                          if (
                            productTitle?.toLowerCase().includes("conveycing")
                          ) {
                            productTitle = productTitle.replace(
                              /conveycing/i,
                              "Conveyancing",
                            );
                          }

                          // If it's a conveyancing product and has service_type, format it properly
                          if (
                            (productTitle
                              ?.toLowerCase()
                              .includes("conveyancing") ||
                              lead?.is_conveyancing) &&
                            lead?.service_type
                          ) {
                            // Always show as "Conveyancing (Service Type)"
                            return `Conveyancing (${lead.service_type.replace(/_/g, " ")})`;
                          }

                          // For other products with service_type
                          if (lead?.service_type && productTitle) {
                            return `${productTitle} (${lead.service_type.replace(/_/g, " ")})`;
                          }

                          return productTitle || "Unknown Product";
                        })()}
                      </td>
                      <td>{lead?.customer_name}</td>
                      {!hideFeeStatus && (
                        <td>
                          {isGeneralEnquiry
                            ? ""
                            : `£${(lead?.total_gross || lead?.net_value || lead?.gross_value || 0).toFixed(2)}`}
                        </td>
                      )}
                      {/* <td>£{(lead?.total_quote || 0).toFixed(2)}</td> */}
                      {!hideFeeStatus && (
                        <td>{isGeneralEnquiry ? "" : lead?.status}</td>
                      )}
                      <td>{new Date(lead?.createdAt).toLocaleString()}</td>
                      {/* <td>{`${lead?.groupcode_name} (${lead?.groupcode_code})`}</td> */}
                      <td>
                        {lead?.groupcode_name &&
                        lead?.groupcode_name !== "Unassigned"
                          ? `${lead.groupcode_name}${lead.groupcode_code ? " (" + lead.groupcode_code + ")" : ""}`
                          : "-"}
                      </td>
                      <td>{lead?.created_by_username}</td>
                      <td>{lead?.contact_attempts}</td>
                      <td>
                        {canViewLeads && (
                          <Tooltip title="View Lead">
                            <NavLink
                              to={
                                lead?.service_type === "Review_Legal_Pack" ||
                                lead?.product_title
                                  ?.toLowerCase()
                                  .includes("review of legal pack")
                                  ? `/leads/view/review-of-legal-pack/${lead?.lead_id}`
                                  : lead?.service_type ===
                                        "First_Registration" ||
                                      lead?.product_title
                                        ?.toLowerCase()
                                        .includes("first registration")
                                    ? `/leads/view/first-registration/${lead?.lead_id}`
                                    : lead?.service_type ===
                                          "Mortgage_Redemption" ||
                                        lead?.product_title
                                          ?.toLowerCase()
                                          .includes("mortgage redemption")
                                      ? `/leads/view/mortgage-redemption/${lead?.lead_id}`
                                      : lead?.service_type === "Remortgage" ||
                                          lead?.product_title
                                            ?.toLowerCase()
                                            .includes("remortgage")
                                        ? `/leads/view/remortgage/${lead?.lead_id}`
                                        : lead?.service_type ===
                                              "Transfer_of_Equity" ||
                                            lead?.product_title
                                              ?.toLowerCase()
                                              .includes("transfer of equity")
                                          ? `/leads/view/transfer-of-equity/${lead?.lead_id}`
                                          : lead?.service_type === "Sale" ||
                                              (lead?.product_title
                                                ?.toLowerCase()
                                                .includes("sale") &&
                                                !lead?.product_title
                                                  ?.toLowerCase()
                                                  .includes("purchase"))
                                            ? `/leads/view/sale/${lead?.lead_id}`
                                            : lead?.service_type ===
                                                  "Purchase_Sale" ||
                                                lead?.service_type ===
                                                  "Sale_Purchase" ||
                                                (lead?.service_type
                                                  ?.toLowerCase()
                                                  .includes("sale") &&
                                                  lead?.service_type
                                                    ?.toLowerCase()
                                                    .includes("purchase")) ||
                                                (lead?.product_title
                                                  ?.toLowerCase()
                                                  .includes("purchase") &&
                                                  lead?.product_title
                                                    ?.toLowerCase()
                                                    .includes("sale"))
                                              ? `/leads/view/sale-purchase/${lead?.lead_id}`
                                              : lead?.service_type ===
                                                    "Purchase" ||
                                                  lead?.product_title
                                                    ?.toLowerCase()
                                                    .includes("purchase")
                                                ? `/leads/view/conveyancing/${lead?.lead_id}`
                                                : lead?.is_conveyancing ||
                                                    lead?.product_title
                                                      ?.toLowerCase()
                                                      .includes(
                                                        "conveyancing",
                                                      ) ||
                                                    lead?.product_title
                                                      ?.toLowerCase()
                                                      .includes("conveycing")
                                                  ? `/leads/view/conveyancing/${lead?.lead_id}`
                                                  : lead?.product_title
                                                        ?.toLowerCase()
                                                        .includes("lss will")
                                                    ? `/leads/view/lss-will/${lead?.lead_id}`
                                                    : lead?.product_title
                                                          ?.toLowerCase()
                                                          .includes(
                                                            "lss lasting powers of attorney",
                                                          )
                                                      ? `/leads/view/lss-lasting-powers-of-attorney/${lead?.lead_id}`
                                                      : lead?.product_title
                                                            ?.toLowerCase()
                                                            .includes(
                                                              "lss estate administration",
                                                            )
                                                        ? `/leads/view/lss-estate-administration/${lead?.lead_id}`
                                                        : lead?.product_title?.toLowerCase() ===
                                                              "will" ||
                                                            lead?.product_title
                                                              ?.toLowerCase()
                                                              .includes(
                                                                "will (single)",
                                                              )
                                                          ? `/leads/view/will/${lead?.lead_id}`
                                                          : `/leads/view/${slugify(lead?.product_title) || "conveyancing"}/${lead?.lead_id}`
                              }
                              className="red-link-text"
                            >
                              View
                            </NavLink>
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 8,
              marginBottom: 8,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <span className="text-muted small">
              Showing{" "}
              {leadTotalDocs === 0 ? 0 : (leadPage - 1) * leadPageSize + 1}-
              {Math.min(leadPage * leadPageSize, leadTotalDocs)} of{" "}
              {leadTotalDocs} leads
            </span>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="lead-page-size-label">Rows per page</InputLabel>
              <Select
                labelId="lead-page-size-label"
                label="Rows per page"
                value={leadPageSize}
                onChange={handleLeadPageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 8 }}
          >
            <Pagination
              count={leadTotalPages}
              page={leadPage}
              onChange={handleLeadPageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{
                "& .Mui-selected": {
                  background:
                    "linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important",
                  color: "#fff !important",
                },
                "& .MuiPaginationItem-root": {
                  color: "var(--bs-red-color)",
                  borderColor: "var(--bs-red-color)",
                },
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
